import styled from "@emotion/styled";
import { PageType, useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import classNames from "classnames";
import { type FunctionComponent, useCallback } from "react";

import type { Props as BaseLinkProps } from "~/components/common/base-link";
import type { ContentSchemaRenderProps } from "~/components/schema-render/content";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { sendGA4Tracking } from "~/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "~/components/tracking/google-analytics-4/types";
import { data as appData, config, tracking } from "~/data";
import { useCurrentPageType } from "~/lib/router/hooks";
import { isAbsoluteUrl } from "~/lib/utils";
import type { contentSchemaRenderContent$data } from "~/queries/__generated__/contentSchemaRenderContent.graphql";

import { AnchorRelationship, AnchorTarget } from "./enums";
import { StyledBaseLink, Text } from "./styles";

type GetQueryParametersModuleParameters = {
  isDrupalAutoLink?: boolean;
  pageType: PageType;
};

const getQueryParametersModule = (parameters: GetQueryParametersModuleParameters) => {
  const { isDrupalAutoLink, pageType } = parameters;

  if (isDrupalAutoLink) {
    return tracking.module.InlineAuto;
  }

  return pageType === PageType.PostiesArticle
    ? tracking.module.InlinePosties
    : tracking.module.Inline;
};

export type Props = {
  className?: string[];
  href?: string;
  reference?: contentSchemaRenderContent$data;
} & ContentSchemaRenderProps;

const Component: FunctionComponent<Props> = ({
  children,
  className,
  href = "",
  parentSchemaNode,
  ...attribs
}) => {
  const regex = new RegExp("(^https?://)((www.)?scmp.com|.*.product-web.dev-2.scmp.tech)");
  const isInternalScmpLink = regex.test(href);
  // Link added in drupal by system config automatically
  const isDrupalAutoLink = className?.includes("d8-auto-link");
  const isDigitalArchiveLink =
    !isDrupalAutoLink &&
    ["p", "h3"].includes(parentSchemaNode?.type ?? "") &&
    parentSchemaNode?.children?.length === 1 &&
    parentSchemaNode?.children?.[0].type === "a" &&
    isInternalScmpLink;

  const currentPageType = useCurrentPageType();
  // Generate link with custom parameters
  const queryParameters: BaseLinkProps["query"] = {
    module: getQueryParametersModule({
      isDrupalAutoLink,
      pageType: currentPageType,
    }),
    pgtype: currentPageType,
  };

  const isInternalUrl =
    isInternalScmpLink ||
    [
      appData.application.scmp.domain,
      appData.application.goldthread.domain,
      config.general.siteUrl,
    ].some(internalUrl => href.includes(internalUrl));
  let anchorTarget: OrUndefined<AnchorTarget>;
  let anchorRelationship: OrUndefined<string>;
  if (!isAbsoluteUrl(href) || !isInternalUrl || className?.includes("js-tracking-referral")) {
    if (!isInternalUrl) anchorRelationship = AnchorRelationship.NoFollow;
    anchorTarget = AnchorTarget.Blank;
  }

  const ga4ImpressionEvent = useCallback<() => RecirculationWidgetImpressionEvent>(() => {
    const result = /\/(?<articleId>\d+)\//.exec(href);
    const articleId =
      isInternalScmpLink && result?.groups?.articleId ? result.groups.articleId : null;

    return {
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        destination_url: href,
        ...(articleId ? { article_id: articleId } : {}),
        page_type: "article",
        widget_name: "inline_link",
      },
      subcategory: "widget",
    };
  }, [href, isInternalScmpLink]);

  const handleGATracking = () => {
    sendGATracking({
      action: "Inline link/Click",
      category: "Article Inline Link",
      label: href,
    });

    const result = /\/(?<articleId>\d+)\//.exec(href);
    const articleId =
      isInternalScmpLink && result?.groups?.articleId ? result.groups.articleId : null;

    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        destination_url: href,
        ...(articleId ? { article_id: articleId } : {}),
        page_type: "article",
        widget_name: "inline_link",
      },
      subcategory: "widget",
    });
  };

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    null,
    RecirculationWidgetImpressionEvent
  >({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: ga4ImpressionEvent,
    options: {
      isSendGA4Tracking: true,
      shouldSendOnce: true,
    },
  });

  if (isDigitalArchiveLink) return null;

  return (
    <StyledBaseLink
      anchorProps={{ rel: anchorRelationship, target: anchorTarget }}
      className={classNames(className)}
      pathname={href}
      query={isInternalUrl ? queryParameters : {}}
      ref={captureTrackImpressionEventTargetElement}
      onClick={handleGATracking}
      {...attribs}
    >
      <Text>{children}</Text>
    </StyledBaseLink>
  );
};

Component.displayName = "Link";
export const Link = styled(Component)``;
